import {
  EmployeeIdentificationType,
  EmploymentPeriodStatusEnum,
  EmploymentViewModelNew,
} from '@fora-monorepo/employer-my-pages-common/types';
import { Assets, Container, Flex, Headings, Link, VerticalSpacer } from '@foradevelopment/fora-ui';

import { AddressDetails, EmploymentPeriodText, PersonDetails } from '../../components';
import { MarkupAndParametersList } from '../../components/EmploymentDetails/MarkupAndParametersList';
import { useTypedTranslation } from '../../i18n/i18n';
import { links } from '../../router/links';
import { getCountryFromCode } from '../../common/utils';
import { useAppSelector } from '../../redux/hooks';

type AddEmployeeConfirmationDetailsProps = {
  resetPanels: () => void;
  employment: EmploymentViewModelNew;
};

export const AddEmployeeConfirmationDetails = ({ employment, resetPanels }: AddEmployeeConfirmationDetailsProps) => {
  const { t } = useTypedTranslation();
  const countries = useAppSelector(state => state.foraGlobal.countries);

  const { employmentPeriods } = employment;
  const { employee } = employment;

  const activeEmploymentPeriod = employmentPeriods.find(period => {
    return period.employmentStatus === EmploymentPeriodStatusEnum.Active;
  });

  return (
    <Container maxWidth={578} width="100%">
      <Assets.CheckmarkIcon color="black" width={30} />
      <VerticalSpacer marginBottom={10} />
      <Headings.H2>{t('panel.employeeAddedOrUpdated.confirmationAddedText')}</Headings.H2>
      <VerticalSpacer marginBottom={60} />
      <PersonDetails {...employee} />
      {employment.policyNumber &&
      employment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber &&
      !employment.employee.isVerified ? (
        <>
          <VerticalSpacer marginBottom={10} />
          <AddressDetails
            {...employee.address}
            country={getCountryFromCode(countries, employment.employee.address.countryCode)}
            singleRow
          />
        </>
      ) : null}
      {employment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber ? (
        <>
          <VerticalSpacer marginBottom={10} />
          <Link to={`${links.editEmployee}/${employment.policyNumber}`} hasIcon={false}>
            {t('section.employeeDetails.editEmployeeLink')} <Assets.EditIcon width={14} marginLeft={6} />
          </Link>
        </>
      ) : null}
      <VerticalSpacer marginBottom={60} />
      <Container>
        <Headings.H3>{t('panel.editEmploymentPeriodConfirmation.employmentPeriodHeading')}</Headings.H3>
        <VerticalSpacer marginBottom={6} />
        {activeEmploymentPeriod?.employmentStartDate ? (
          <>
            <EmploymentPeriodText
              startDate={activeEmploymentPeriod?.employmentStartDate}
              endDate={activeEmploymentPeriod?.employmentEndDate}
            />
            <VerticalSpacer marginBottom={10} />
            <Link to={`${links.editEmploymentPeriod}/${employment.policyNumber}/0`} hasIcon={false}>
              {t('section.employmentDetails.editEmployment')} <Assets.EditIcon width={14} marginLeft={6} />
            </Link>
          </>
        ) : null}
        <VerticalSpacer marginBottom={30} />
        {activeEmploymentPeriod && (
          <MarkupAndParametersList
            employmentPeriodStatus={activeEmploymentPeriod?.employmentStatus}
            markups={activeEmploymentPeriod?.markups}
            fokParameters={activeEmploymentPeriod.fokParameterChoices}
          />
        )}
      </Container>
      <VerticalSpacer marginBottom={60} />
      <Container>
        <Flex rowGap={20} columnGap={20} flexWrap="wrap">
          <Link styleAs="button" to={links.addEmployee} onClick={resetPanels} hasIcon={false}>
            {t('panel.employeeAddedOrUpdated.buttonTextAddMoreEmployments')}
          </Link>
          <Link variant="outline" styleAs="button" to={links.employments} hasIcon={false}>
            {t('panel.employeeAddedOrUpdated.buttonTextShowAllEmployments')}
          </Link>
        </Flex>
      </Container>
    </Container>
  );
};
